/**
 * lastModule.ts — the module you were last in, remembered across pages and reloads.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  ⛔ THIS IS NEW. THE APP REMEMBERED NOTHING ABOUT THE LAST MODULE BEFORE.     ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 * Lis's brief, 2026-10-06, for the module section on `/mail`: *"show the module I was
 * last in (use whatever the app already remembers about the last module, and tell me
 * what that is); if there is none, show the first module the person can access."*
 *
 * **There was nothing to reuse.** Searched before writing a line: no `lastModule`, no
 * route history, no `sessionStorage` entry, and the twelve `pulsehq_*` localStorage keys
 * are all appearance, workspace or tour state. The dock's active tile was derived fresh
 * from `location.pathname` on every render and thrown away. So *"whatever the app already
 * remembers"* was **nothing**, and the rule's first clause had no way to fire — every
 * visit would have fallen to *"the first module the person can access"*, which is the
 * fallback, not the rule.
 *
 * This file is the smallest thing that makes the first clause real: one string, written
 * when you are genuinely inside a module, read when you are somewhere that has none.
 *
 * ── WHY `localStorage` AND NOT STATE ────────────────────────────────────────────
 * *"The module I was last in"* survives a reload, and a React state does not. The cost is
 * the usual one: this is per-browser, it can be empty, and the accessor can throw in a
 * private window or with site data blocked. Every read and write is wrapped, and a failed
 * read returns `null` — which is the same path as a first-ever visit, so there is no
 * branch here that only runs when storage works.
 *
 * ⚠️ **THE VALUE IS VALIDATED ON READ, NOT TRUSTED.** It is a string in a bucket anyone can
 * edit, and the dock's key list changes as modules come and go. A value that is not a
 * current `DockKey` is treated as absent rather than passed on — and ACCESS is checked by
 * the caller afterwards, because this file knows nothing about who is signed in.
 */

import { DOCK, type DockKey } from '../components/layout/dock';

/** The one key. Named like the other `pulsehq_*` entries already in localStorage. */
export const LAST_MODULE_STORAGE_KEY = 'pulsehq_last_module';

/** Every key the dock currently has. Read once — `DOCK` is a module-level constant. */
const VALID: ReadonlySet<string> = new Set(DOCK.map(d => d.key));

/**
 * The remembered module, or `null`.
 *
 * `null` covers all three of: never set, storage unreadable, and a value that is no
 * longer a dock key. The caller treats them identically, which is why they are not
 * distinguished here.
 */
export function readLastModule(): DockKey | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(LAST_MODULE_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw || !VALID.has(raw)) return null;
  return raw as DockKey;
}

/**
 * Remember the module the person is in.
 *
 * ⚠️ Call this ONLY with a key derived from the route they are actually on. Writing a
 * fallback back into storage would make the fallback self-perpetuating: the first module
 * the person can access would be recorded as somewhere they had "been", and the real
 * answer would never get a chance to replace it.
 */
export function rememberLastModule(key: DockKey): void {
  if (!VALID.has(key)) return;
  try {
    window.localStorage.setItem(LAST_MODULE_STORAGE_KEY, key);
  } catch {
    /* Private window, blocked site data, quota. The feature degrades to the fallback,
       which is a correct answer rather than a broken one. */
  }
}
